import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import Supplier from "../../models/supplier";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import SupplierDeleteModal from "./SupplierDeleteModal";
import SupplierUpdateModal from "./SupplierUpdateModal";
import {
  SUPPLIERS_CREATE,
  SUPPLIERS_DESTROY,
  SUPPLIERS_UPDATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { emptyValueOnUndefinedOrNull } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

const ITEMS_PER_PAGE = 10;

const SupplierList = () => {
  const fields = [
    {
      key: "fantasyName",
      _classes: ["text-center"],
      label: "Nombre de Fantasía",
    },
    {
      key: "legalName",
      _classes: ["text-center"],
      label: "Nombre Legal",
    },
    {
      key: "ruc",
      _classes: ["text-center"],
      label: "RUC",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [showSupplierEditModal, setShowSupplierEditModal] = useState(false);
  const [showSupplierDeleteModal, setShowSupplierDeleteModal] = useState(false);

  const [supplier, setSupplier] = useState<Supplier | undefined>(undefined);
  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchSuppliers = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const additionalParams = new Map<string, string>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());
    const requestStatus = await getList<Supplier>(
      "/suppliers/",
      limit,
      offset,
      additionalParams,
    );
    if (requestStatus.status === SUCCESS) {
      if (requestStatus.data !== undefined) {
        const count = requestStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setSuppliers(requestStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = requestStatus.detail
        ? requestStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : "",
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowSupplierEditModal(true);
  };

  const onUpdateClick = (supplier: Supplier) => {
    setSupplier(supplier);
    setShowSupplierEditModal(true);
  };

  const onDeleteClick = (supplier: Supplier) => {
    setSupplier(supplier);
    setShowSupplierDeleteModal(true);
  };

  const onSupplierEditCancel = () => {
    setSupplier(undefined);
    setShowSupplierEditModal(false);
  };

  const onSupplierEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowSupplierEditModal(false);
    setSuppliers([]);
    setLoading(true);
    fetchSuppliers();
  };

  const onSupplierDeleteCancel = () => {
    setSupplier(undefined);
    setShowSupplierDeleteModal(false);
  };

  const onSupplierDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowSupplierDeleteModal(false);
    setSuppliers([]);
    setLoading(true);
    fetchSuppliers();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("suppliers")) {
        return;
      }
      fetchSuppliers();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Proveedores</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent permissionName={SUPPLIERS_CREATE}>
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={{
                    noItems: "No se encontraron registros",
                    noResults: "No se encontraron registros",
                  }}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={suppliers}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    fantasyName: (item: Supplier) => {
                      return (
                        <td className="text-center">
                          {emptyValueOnUndefinedOrNull(item.fantasyName)}
                        </td>
                      );
                    },
                    legalName: (item: Supplier) => {
                      return (
                        <td className="text-center">
                          {emptyValueOnUndefinedOrNull(item.legalName)}
                        </td>
                      );
                    },
                    ruc: (item: Supplier) => {
                      return (
                        <td className="text-center">
                          {emptyValueOnUndefinedOrNull(item.ruc)}
                        </td>
                      );
                    },
                    actions: (item: Supplier) => {
                      const editButton = (
                        <PermissionRequiredComponent
                          permissionName={SUPPLIERS_UPDATE}
                        >
                          <CButton
                            className="text-white"
                            color="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );
                      const deleteButton = (
                        <PermissionRequiredComponent
                          permissionName={SUPPLIERS_DESTROY}
                        >
                          <CButton
                            className="text-white"
                            color="danger"
                            onClick={() => {
                              onDeleteClick(item);
                            }}
                          >
                            <i className="fa fa-trash"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <SupplierUpdateModal
        show={showSupplierEditModal}
        onCancel={onSupplierEditCancel}
        onSuccess={onSupplierEditSuccess}
        supplier={supplier}
      />
      <SupplierDeleteModal
        show={showSupplierDeleteModal}
        onCancel={onSupplierDeleteCancel}
        onSuccess={onSupplierDeleteSuccess}
        supplier={supplier}
      />
    </>
  );
};

export default SupplierList;
